import type { RemoteSupportWidgetUser } from '@/modules/common/services/remote-support-widget.service'
import { uuidToInt } from '@/modules/common/utils/uuid-to-int.util'

export type RemoteSupportWidgetState = 'none' | 'pending' | 'general' | 'remote'

/**
 * 'none' outside event context; 'pending' while the loaded event hasn't caught up to the
 * route yet (avoids showing the previous event's widget mid-switch, matching the same
 * event.uuid !== route.params.event_uuid guard used in event.layout.ts/module.layout.ts);
 * otherwise the widget variant to show for the current event's remote_support_enabled flag.
 */
export function resolveRemoteSupportWidgetState (routeParams: { uuid?: string; event_uuid?: string }, event: Record<string, any> | undefined): RemoteSupportWidgetState {
  if (!routeParams.uuid || !routeParams.event_uuid) {
    return 'none'
  }
  if (!event || event.uuid !== routeParams.event_uuid) {
    return 'pending'
  }
  return event.remote_support_enabled ? 'remote' : 'general'
}

/**
 * Maps the logged-in admin (Vuex security/currentUser) to the widget's per-user data-ft-*
 * attributes. Returns undefined when no user is loaded yet, since the widget re-identifies
 * the current admin and shouldn't mount with empty identity fields.
 *
 * Forethought's data-ft-account-Id context variable requires an integer, but admins are only
 * ever identified by UUID elsewhere in this app - uuidToInt derives a stable integer from the
 * UUID rather than exposing the internal numeric id (which isn't sent to the frontend at all).
 */
export function resolveRemoteSupportWidgetUser (currentUser: Record<string, any> | undefined): RemoteSupportWidgetUser | undefined {
  if (!currentUser?.uuid) {
    return undefined
  }
  return {
    accountId: String(uuidToInt(currentUser.uuid)),
    email: currentUser.email,
    fullName: [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ')
  }
}
